// Wrapped local store for named, user-saved product configurations.
//
// On-disk format (per key): Array<StoredEntry<R>>.
//
// Migration is transparent. If a key still holds the legacy bare-request
// array (Array<R>), reads upgrade it in place to Array<StoredEntry<R>>
// using generated UUIDs (no names), and write it back so subsequent reads
// see the new shape.
//
// Lookup by id falls back to numeric-position lookup when no wrapper id
// matches. This keeps already-open detail-page URLs (/products/.../0)
// working through the migration: the page resolves the entry, then saves
// back under the migrated UUID and updates its route.
//
// Import accepts either shape (bare requests or wrappers) and normalises
// to wrappers, so existing backup files round-trip cleanly.

import { generateId } from './dataStore';

export interface StoredEntry<R> {
  id: string;
  name?: string;
  createdAt: string;
  updatedAt: string;
  request: R;
}

export interface SaveOptions {
  id?: string;
  name?: string;
}

export interface WrappedStore<R> {
  getAll(): Promise<StoredEntry<R>[]>;
  getById(id: string): Promise<StoredEntry<R> | null>;
  getRequestById(id: string): Promise<R | null>;
  save(request: R, opts?: SaveOptions): Promise<string>;
  delete(id: string): Promise<void>;
  clear(): Promise<void>;
  replaceAll(entries: Array<R | StoredEntry<R>>): Promise<void>;
  deriveName(request: R): string;
  exportJson(): Promise<string>;
  importJson(text: string): Promise<StoredEntry<R>[]>;
}

export interface WrappedStoreOptions<R> {
  storageKey: string;
  deriveName: (request: R) => string;
  // Optional side effect run after any mutation that changes the persisted
  // contents. Used e.g. to backfill inflation indices when inflation swaps
  // change. Mirrors the original module-level hook.
  onChange?: (entries: StoredEntry<R>[]) => void | Promise<void>;
}

function isWrapped<R>(value: any): value is StoredEntry<R> {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof value.id === 'string' &&
    'request' in value
  );
}

function nowIso(): string {
  return new Date().toISOString();
}

function readRaw(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeRaw<T>(key: string, value: T) {
  localStorage.setItem(key, JSON.stringify(value));
}

function normaliseInto<R>(value: unknown): { entries: StoredEntry<R>[]; migrated: boolean } {
  if (!Array.isArray(value)) return { entries: [], migrated: false };
  let migrated = false;
  const entries: StoredEntry<R>[] = [];
  for (const item of value) {
    if (isWrapped<R>(item)) {
      entries.push({
        id: item.id || generateId(),
        name: typeof item.name === 'string' && item.name.length > 0 ? item.name : undefined,
        createdAt: item.createdAt || nowIso(),
        updatedAt: item.updatedAt || item.createdAt || nowIso(),
        request: item.request,
      });
      if (!item.id) migrated = true;
    } else if (item && typeof item === 'object') {
      // Legacy bare request — wrap it.
      entries.push({
        id: generateId(),
        createdAt: nowIso(),
        updatedAt: nowIso(),
        request: item as R,
      });
      migrated = true;
    }
  }
  return { entries, migrated };
}

export function createWrappedStore<R>(opts: WrappedStoreOptions<R>): WrappedStore<R> {
  const { storageKey, deriveName, onChange } = opts;

  function loadAndMigrate(): StoredEntry<R>[] {
    const raw = readRaw(storageKey);
    const { entries, migrated } = normaliseInto<R>(raw);
    if (migrated) writeRaw(storageKey, entries);
    return entries;
  }

  function persist(entries: StoredEntry<R>[]): void {
    writeRaw(storageKey, entries);
  }

  async function fireOnChange(entries: StoredEntry<R>[]): Promise<void> {
    if (!onChange) return;
    try {
      await onChange(entries);
    } catch (err) {
      console.warn(`[${storageKey}] onChange hook failed`, err);
    }
  }

  return {
    async getAll() {
      const entries = loadAndMigrate();
      await fireOnChange(entries);
      return entries;
    },

    async getById(id: string) {
      const entries = loadAndMigrate();
      const direct = entries.find(e => e.id === id);
      if (direct) return direct;
      // Legacy URL fallback: numeric position lookup.
      const idx = Number(id);
      if (!Number.isNaN(idx) && idx >= 0 && idx < entries.length) {
        return entries[idx];
      }
      return null;
    },

    async getRequestById(id: string) {
      const entry = await this.getById(id);
      return entry?.request ?? null;
    },

    async save(request: R, options?: SaveOptions) {
      const entries = loadAndMigrate();
      const now = nowIso();
      const incomingId = options?.id;
      const incomingName = options?.name?.trim() || undefined;

      let targetIndex = -1;
      if (incomingId) {
        targetIndex = entries.findIndex(e => e.id === incomingId);
        if (targetIndex === -1) {
          const idx = Number(incomingId);
          if (!Number.isNaN(idx) && idx >= 0 && idx < entries.length) {
            targetIndex = idx;
          }
        }
      }

      let savedId: string;
      if (targetIndex !== -1) {
        const existing = entries[targetIndex];
        const next: StoredEntry<R> = {
          id: existing.id,
          name: incomingName !== undefined ? incomingName : existing.name,
          createdAt: existing.createdAt || now,
          updatedAt: now,
          request,
        };
        entries[targetIndex] = next;
        savedId = next.id;
      } else {
        const next: StoredEntry<R> = {
          id: incomingId && !entries.some(e => e.id === incomingId) ? incomingId : generateId(),
          name: incomingName,
          createdAt: now,
          updatedAt: now,
          request,
        };
        entries.push(next);
        savedId = next.id;
      }

      persist(entries);
      await fireOnChange(entries);
      return savedId;
    },

    async delete(id: string) {
      const entries = loadAndMigrate();
      const filtered = entries.filter(e => e.id !== id);
      if (filtered.length === entries.length) {
        const idx = Number(id);
        if (!Number.isNaN(idx) && idx >= 0 && idx < entries.length) {
          entries.splice(idx, 1);
          persist(entries);
          await fireOnChange(entries);
          return;
        }
      }
      persist(filtered);
      await fireOnChange(filtered);
    },

    async clear() {
      localStorage.removeItem(storageKey);
      await fireOnChange([]);
    },

    async replaceAll(items) {
      const { entries } = normaliseInto<R>(items);
      persist(entries);
      await fireOnChange(entries);
    },

    deriveName,

    async exportJson() {
      const entries = await this.getAll();
      return JSON.stringify(entries, null, 2);
    },

    async importJson(text: string) {
      const data = JSON.parse(text);
      const { entries } = normaliseInto<R>(data);
      if (entries.length === 0) throw new Error('No entries found in import file');
      persist(entries);
      await fireOnChange(entries);
      return entries;
    },
  };
}

// Helper used by storage modules that need to emit a one-line filename
// for a downloaded JSON file. Centralised so call sites don't repeat
// the date suffix pattern.
export function downloadJson(filename: string, payload: unknown) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
